import { useMemo } from "react";
import { API } from "@/lib/api";

const COLORS = [
  "#0047FF", "#7c3aed", "#059669", "#f59e0b", "#dc2626",
  "#0891b2", "#db2777", "#65a30d", "#ea580c", "#9333ea",
];

function pickColor(seed = "") {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = ((h << 5) - h) + seed.charCodeAt(i);
  return COLORS[Math.abs(h) % COLORS.length];
}

function initials(first = "", last = "", email = "") {
  const f = (first || "").trim().charAt(0);
  const l = (last || "").trim().charAt(0);
  if (f || l) return (f + l).toUpperCase();
  const handle = (email || "").split("@")[0];
  return (handle.charAt(0) + (handle.charAt(1) || "")).toUpperCase() || "?";
}

/**
 * Universal avatar — renders the user's uploaded picture if present, otherwise
 * a colored circle with their initials. Used in nav, account, pickup screen,
 * driver views, etc.
 *
 * Props:
 *   user: { first_name, last_name, email, avatar_url }
 *   size: pixel diameter (default 40)
 *   ring: optional ring color class (e.g. "ring-2 ring-[#0047FF]/40")
 */
export default function UserAvatar({ user, size = 40, ring = "", className = "", testId }) {
  const u = user || {};
  const src = u.avatar_url
    ? (u.avatar_url.startsWith("http") ? u.avatar_url : `${API.replace(/\/api$/, "")}${u.avatar_url}`)
    : null;
  const inits = useMemo(() => initials(u.first_name, u.last_name, u.email), [u.first_name, u.last_name, u.email]);
  const bg = useMemo(() => pickColor(u.email || u.id || inits), [u.email, u.id, inits]);

  if (src) {
    return (
      <img
        src={src}
        alt={inits}
        width={size}
        height={size}
        loading="lazy"
        className={`rounded-full object-cover bg-[#121212] ${ring} ${className}`}
        style={{ width: size, height: size }}
        data-testid={testId}
      />
    );
  }
  return (
    <div
      className={`rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 ${ring} ${className}`}
      style={{ width: size, height: size, background: bg, fontSize: size * 0.4, fontFamily: "Outfit, system-ui" }}
      data-testid={testId}
    >
      {inits}
    </div>
  );
}
